import { type ReactElement, useId } from 'react';
import { Link } from 'react-router';
import { PRIVACY_POLICY_PATH } from '@/shared/config';

export interface GuestPrivacyConsentProps {
  readonly checked: boolean;
  readonly error?: string | undefined;
  readonly onChange: (checked: boolean) => void;
}

/**
 * 안내 표 — 항목·목적·기간(비회원 견적 설계 G4·G17). 동의 기록에는 버전(`GUEST_QUOTE_CONSENT_VERSION`)이 저장값으로만 남는다.
 * 개인정보 처리방침의 비회원 견적 항목과 같은 내용이어야 한다(Task 14 문서).
 */
const NOTICE_ROWS = [
  {
    term: '수집 항목',
    detail:
      '이메일 또는 휴대폰 번호, 회사·단체명, 담당자명, 요청 내용(디자인·수량·메모), 배송지 주소(우편번호·기본주소)와 배송지별 수량, 접속 IP·브라우저 정보',
  },
  {
    term: '이용 목적',
    detail: '견적 상담 연락과 견적서 전달, 주문할 때 회원 계정에 견적 연결',
  },
  {
    term: '보유 기간',
    detail: '접수일로부터 1년. 견적을 회원 계정에 연결하면 회원 정보 보유 기간을 따릅니다.',
  },
] as const;

/**
 * 비회원 견적 상담 개인정보 수집·이용 동의(필수). 동의 체크는 줄 전체가 라벨이라 44px 이상을 누를 수 있다.
 * 체크 전에는 폼이 [상담 요청]을 잠근다(잠금은 폼 몫, 여기는 표시와 값만).
 */
export const GuestPrivacyConsent = ({
  checked,
  error,
  onChange,
}: GuestPrivacyConsentProps): ReactElement => {
  const errorId = useId();

  return (
    <div className="flex flex-col gap-4">
      <section
        aria-label="개인정보 수집·이용 안내"
        className="flex flex-col gap-4 border border-foreground/10 bg-muted/30 px-5 py-4"
      >
        <p className="text-sm font-semibold">비회원 견적 상담 개인정보 수집·이용 안내</p>
        <dl className="grid gap-x-4 gap-y-2 text-xs leading-relaxed sm:grid-cols-[5.5rem_minmax(0,1fr)]">
          {NOTICE_ROWS.map((row) => (
            <div key={row.term} className="contents">
              <dt className="font-medium text-muted-foreground">{row.term}</dt>
              <dd>{row.detail}</dd>
            </div>
          ))}
        </dl>
        <p className="text-xs leading-relaxed text-muted-foreground">
          동의하지 않을 수 있지만, 동의하지 않으면 비회원 견적 상담을 요청할 수 없어요. 자세한
          내용은{' '}
          <Link to={PRIVACY_POLICY_PATH} className="underline underline-offset-4">
            개인정보 처리방침
          </Link>
          에서 확인할 수 있어요.
        </p>
      </section>

      <label className="flex min-h-11 cursor-pointer items-center gap-3 text-sm font-medium">
        <input
          type="checkbox"
          checked={checked}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className="size-5 shrink-0 cursor-pointer accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onChange={(event) => onChange(event.target.checked)}
        />
        개인정보 수집·이용에 동의합니다 (필수)
      </label>
      {error ? (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
};
